import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import httpRequest from '../api';

const SubscriptionSettings = () => {
    const { tenant } = useSelector((state) => state.tenant);
    const [loading, setLoading] = useState(false);

    if (!tenant) {
        return <div style={loadingStyle}>Cargando información de suscripción...</div>;
    }

    const now = new Date();
    const expiresAt = tenant.subscriptionExpiresAt ? new Date(tenant.subscriptionExpiresAt) : null;
    const daysRemaining = expiresAt ? Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24)) : null;
    const isExpired = daysRemaining !== null && daysRemaining < 0;
    const isExpiringSoon = daysRemaining !== null && daysRemaining > 0 && daysRemaining <= 30;

    // Debug: ver qué datos tiene el tenant
    console.log('🔍 Tenant data:', {
        id: tenant._id,
        subscriptionType: tenant.subscriptionType,
        plan: tenant.plan,
        expiresAt: tenant.subscriptionExpiresAt
    });

    const handlePayWithMP = async () => {
        try {
            setLoading(true);
            const tenantId = tenant._id || tenant.id;
            console.log('💳 Generando link de pago para tenant:', tenantId);

            const response = await httpRequest.post('/subscriptions/generate-link', {
                tenantId,
                planType: tenant.subscriptionType || 'monthly'
            });

            if (response.data.ok && response.data.paymentLink) {
                // Redirigir a Mercado Pago
                window.location.href = response.data.paymentLink;
            }
        } catch (error) {
            const message = error.response?.data?.message || 'Error al generar link de pago';
            alert('❌ ' + message);
            setLoading(false);
        }
    };

    const handleRequestRenewal = async () => {
        try {
            const tenantId = tenant._id || tenant.id;
            const response = await httpRequest.post('/subscriptions/request', {
                tenantId,
                planType: tenant.subscriptionType || 'monthly',
                contactInfo: {
                    name: tenant.businessName,
                    email: tenant.email,
                    phone: tenant.phone || ''
                },
                message: 'Solicitud de renovación de suscripción'
            });

            if (response.data.ok) {
                alert('✅ Solicitud enviada correctamente. Te contactaremos pronto.');
            }
        } catch (error) {
            const message = error.response?.data?.message || 'Error al enviar solicitud';
            alert('❌ ' + message);
        }
    };

    return (
        <div style={containerStyle}>
            <div style={cardStyle}>
                <h3 style={titleStyle}>📊 Estado de Suscripción</h3>

                <div style={infoGridStyle}>
                    <div style={infoItemStyle}>
                        <span style={labelStyle}>Plan Actual:</span>
                        <span style={valueStyle}>{tenant.plan?.toUpperCase() || 'FREE'}</span>
                    </div>

                    <div style={infoItemStyle}>
                        <span style={labelStyle}>Tipo de Suscripción:</span>
                        <span style={valueStyle}>
                            {tenant.subscriptionType === 'monthly' && '📅 Mensual'}
                            {tenant.subscriptionType === 'annual' && '📆 Anual'}
                            {tenant.subscriptionType === 'manual' && '✋ Manual'}
                            {!tenant.subscriptionType && '—'}
                        </span>
                    </div>

                    <div style={infoItemStyle}>
                        <span style={labelStyle}>Estado:</span>
                        <span style={{
                            ...valueStyle,
                            ...statusBadgeStyle,
                            background: tenant.subscriptionStatus === 'active' ? '#d4edda' : '#f8d7da',
                            color: tenant.subscriptionStatus === 'active' ? '#155724' : '#721c24'
                        }}>
                            {tenant.subscriptionStatus === 'active' && '✅ Activa'}
                            {tenant.subscriptionStatus === 'trial' && '🔄 Prueba'}
                            {tenant.subscriptionStatus === 'cancelled' && '❌ Cancelada'}
                            {tenant.subscriptionStatus === 'paused' && '⏸️ Pausada'}
                            {!tenant.subscriptionStatus && '—'}
                        </span>
                    </div>

                    {expiresAt && (
                        <div style={infoItemStyle}>
                            <span style={labelStyle}>Vence:</span>
                            <span style={valueStyle}>{expiresAt.toLocaleDateString('es-AR')}</span>
                        </div>
                    )}

                    {daysRemaining !== null && (
                        <div style={infoItemStyle}>
                            <span style={labelStyle}>Días Restantes:</span>
                            <span style={{
                                ...valueStyle,
                                fontWeight: 700,
                                color: isExpired ? '#dc3545' : isExpiringSoon ? '#fd7e14' : '#28a745'
                            }}>
                                {isExpired ? `Vencida hace ${Math.abs(daysRemaining)} días` : `${daysRemaining} días`}
                            </span>
                        </div>
                    )}

                    <div style={infoItemStyle}>
                        <span style={labelStyle}>Renovación Automática:</span>
                        <span style={valueStyle}>
                            {tenant.subscriptionRenewsAutomatically ? '✅ Sí' : '❌ No'}
                        </span>
                    </div>
                </div>

                {(isExpired || isExpiringSoon) && (
                    <div style={{
                        ...alertBoxStyle,
                        background: isExpired ? '#f8d7da' : '#fff3cd',
                        borderColor: isExpired ? '#f5c6cb' : '#ffeaa7'
                    }}>
                        <strong>{isExpired ? '⚠️ Suscripción Vencida' : '⏰ Suscripción por Vencer'}</strong>
                        <p style={{ margin: '8px 0 0', fontSize: 13 }}>
                            {isExpired
                                ? 'Tu suscripción ha vencido. Algunas funciones pueden estar bloqueadas.'
                                : `Tu suscripción vence en ${daysRemaining} días. Renueva pronto para evitar interrupciones.`
                            }
                        </p>
                    </div>
                )}

                <div style={actionsStyle}>
                    {(tenant.subscriptionType === 'monthly' || tenant.subscriptionType === 'annual') && (
                        <button
                            onClick={handlePayWithMP}
                            style={{ ...primaryButtonStyle, opacity: loading ? 0.6 : 1 }}
                            disabled={loading}
                        >
                            {loading ? '⏳ Generando link...' : '💳 Pagar con Mercado Pago'}
                        </button>
                    )}
                    {tenant.subscriptionType === 'manual' && (
                        <button onClick={handleRequestRenewal} style={primaryButtonStyle}>
                            🔄 Solicitar Renovación Manual
                        </button>
                    )}
                    <button
                        onClick={() => window.open('mailto:ventiopos@gmail.com?subject=Consulta sobre suscripción', '_blank')}
                        style={secondaryButtonStyle}
                    >
                        📧 Contactar Soporte
                    </button>
                </div>
            </div>

            {tenant.blockedFeatures && tenant.blockedFeatures.length > 0 && (
                <div style={cardStyle}>
                    <h3 style={titleStyle}>🚫 Funciones Bloqueadas</h3>
                    <p style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
                        Las siguientes funciones están bloqueadas debido al estado de tu suscripción:
                    </p>
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {tenant.blockedFeatures.map((feature, idx) => (
                            <li key={idx} style={{ fontSize: 14, color: '#dc3545', marginBottom: 4 }}>
                                {feature === 'afip' && '📄 Facturación AFIP'}
                                {feature === 'reports' && '📊 Reportes Avanzados'}
                                {feature === 'exports' && '📥 Exportaciones'}
                                {feature !== 'afip' && feature !== 'reports' && feature !== 'exports' && feature}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

// Estilos
const containerStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: 20
};

const cardStyle = {
    background: '#f8f9fa',
    border: '1px solid #dee2e6',
    borderRadius: 12,
    padding: 20
};

const titleStyle = {
    margin: '0 0 16px',
    fontSize: 16,
    fontWeight: 700,
    color: '#212529'
};

const infoGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 16,
    marginBottom: 20
};

const infoItemStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: 4
};

const labelStyle = {
    fontSize: 12,
    color: '#6c757d',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
};

const valueStyle = {
    fontSize: 15,
    color: '#212529',
    fontWeight: 500
};

const statusBadgeStyle = {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 600,
    width: 'fit-content'
};

const alertBoxStyle = {
    padding: 16,
    borderRadius: 8,
    border: '1px solid',
    marginBottom: 20,
    fontSize: 14
};

const actionsStyle = {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap'
};

const primaryButtonStyle = {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    padding: '12px 24px',
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 600,
    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
    transition: 'transform 0.2s'
};

const secondaryButtonStyle = {
    background: 'white',
    color: '#667eea',
    border: '2px solid #667eea',
    padding: '12px 24px',
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 600,
    transition: 'all 0.2s'
};

const loadingStyle = {
    padding: 40,
    textAlign: 'center',
    color: '#6c757d',
    fontSize: 14
};

export default SubscriptionSettings;
